<template>
  <b-container class="measurements">
    <b-button variant="outline" size="sm" style="float: left"
      ><router-link to="/sessions">
        &#60; Back to sessions</router-link
      ></b-button
    >
    <div class="section" id="header">
      <h1>Session {{ this.$route.params.sessionName }} measurements</h1>
      <small class="text-muted">{{ getMeasurementDateInterval() }}</small>
      <br />
      <small class="text-muted"
        >Measurements: {{ allMeasurements.length }}</small
      >
    </div>

    <div class="section">
      <line-chart
        id="chart-container"
        :chartData="chartData"
        :options="chartOptions"
        v-if="loaded"
      ></line-chart>
      <b-spinner v-else label="Spinning"></b-spinner>
    </div>

    <div class="section">
      <b-button size="sm" v-b-toggle.collapse-1 variant="primary"
        ><small>Chart settings</small></b-button
      >
      <b-collapse id="collapse-1" class="mt-2">
        <b-button-group class="mt-3">
          <b-button
            size="sm"
            variant="light"
            v-on:click="toggleChartXAxisStyle()"
          >
            Date axis style: {{ chartXAxisStyle }}</b-button
          >

          <b-button
            size="sm"
            :variant="showMoisture ? 'primary' : 'secondary'"
            v-on:click="
              {
                {
                  toggleShowMoisture();
                }
              }
            "
          >
            Show moisture points</b-button
          >

          <b-button
            size="sm"
            :variant="showMoistureAverage ? 'primary' : 'secondary'"
            v-on:click="
              {
                {
                  toggleShowMoistureAverage();
                }
              }
            "
          >
            Show moisture average line</b-button
          >

          <b-button
            size="sm"
            :variant="showProtein ? 'primary' : 'secondary'"
            v-on:click="
              {
                {
                  toggleShowProtein();
                }
              }
            "
          >
            Show protein points</b-button
          >

          <b-button
            size="sm"
            :variant="showProteinAverage ? 'primary' : 'secondary'"
            v-on:click="
              {
                {
                  toggleShowProteinAverage();
                }
              }
            "
          >
            Show protein average line</b-button
          >
        </b-button-group>

        <b-form @submit="onSubmit" inline class="mt-4">
          <b-form-group
            label="Moving average step"
            label-for="moving-average"
            class="mr-2 ml-2"
          >
            <b-form-input
              id="moving-average"
              type="number"
              min="1"
              size="sm"
              class="mr-2 ml-2"
              v-model="form.movingAverageStep"
            ></b-form-input>
          </b-form-group>
          <b-button size="sm" type="submit" variant="secondary"
            >Update</b-button
          >
        </b-form>
      </b-collapse>
    </div>

    <div class="section">
      <histogram
        :compoundData="proteinData"
        name="Protein"
        barColor="rgb(255, 99, 132, 0.7)"
        barBorderColor="rgb(255, 99, 132, 1)"
      ></histogram>
    </div>

    <div class="section">
      <histogram
        :compoundData="moistureData"
        name="Moisture"
        barColor="rgb(54, 162, 235, 0.7)"
        barBorderColor="rgb(54, 162, 235, 1)"
      ></histogram>
    </div>

    <div class="section">
      <h1>Results</h1>
      <b-table
        id="allMeasurements"
        small
        hover
        table-variant
        :items="allMeasurements"
        :fields="fields"
      ></b-table>
    </div>
  </b-container>
</template>

<script>
import * as firebase from 'firebase/app';
import 'firebase/auth';
import firebasedb from 'firebase';
import LineChart from '../LineChart.js';
import Histogram from '../components/Histogram';
import moment from 'moment';
import Constants from '../config';

export default {
  components: {
    LineChart,
    Histogram,
  },
  data() {
    return {
      fields: [
        {
          key: 'datestring',
          label: 'Date',
          sortable: true,
        },
        {
          key: 'protein_rounded',
          label: 'Protein %',
          sortable: true,
        },
        {
          key: 'moisture_rounded',
          label: 'Moisture %',
          sortable: true,
        },
      ],
      allMeasurements: [],
      chartData: null,
      chartOptions: null,
      proteinData: null,
      moistureData: null,
      loaded: false,
      chartXAxisStyle: 'linear', // 'linear' or 'series'
      unsubscribeFirestore: null,
      showMoisture: true,
      showProtein: true,
      showMoistureAverage: true,
      showProteinAverage: true,
      form: {
        movingAverageStep: Constants.MOVING_AVERAGE_STEP,
      },
    };
  },
  created() {
    // console.log('Measurement component created');
  },
  destroyed() {
    this.unsubscribeFirestore();
  },
  mounted() {
    const currentUserId = firebase.auth().currentUser.uid;
    const db = firebasedb.firestore();
    const userSessionsRef = db
      .collection(currentUserId)
      .doc(this.$route.params.sessionName)
      .collection('measurements')
      .orderBy('measured_at', 'asc');

    this.unsubscribeFirestore = userSessionsRef.onSnapshot((snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const data = change.doc.data();
          const date = this.convertToDate(data['measured_at']);

          const protein = data['percentages']['protein'] * 100;
          const moisture = data['percentages']['moisture'] * 100;

          let newMeasurement = {
            id: change.doc.id,
            date: date,
            protein: protein,
            moisture: moisture,
            protein_rounded: protein.toFixed(1),
            moisture_rounded: moisture.toFixed(1),
            datestring: date.format('D.MM.YYYY HH:mm:ss'),
            isValid: this.isValidMeasurement(data),
          };

          if (!newMeasurement.isValid) {
            newMeasurement['_rowVariant'] = 'danger';
          }

          this.allMeasurements.unshift(newMeasurement);
        }
      });
      this.fillData();
    });
  },
  methods: {
    onSubmit(evt) {
      evt.preventDefault();
      this.chartOptions = null;
      this.fillData();
    },
    toggleShowProteinAverage: function() {
      this.showProteinAverage = !this.showProteinAverage;
      this.chartOptions = null;
      this.fillData();
    },
    toggleShowMoistureAverage: function() {
      this.showMoistureAverage = !this.showMoistureAverage;
      this.chartOptions = null;
      this.fillData();
    },
    toggleShowMoisture: function() {
      this.showMoisture = !this.showMoisture;
      this.chartOptions = null;
      this.fillData();
    },
    toggleShowProtein: function() {
      this.showProtein = !this.showProtein;
      this.chartOptions = null;
      this.fillData();
    },
    toggleChartXAxisStyle: function() {
      const style = this.chartXAxisStyle;
      this.chartXAxisStyle = style == 'linear' ? 'series' : 'linear';
      this.chartOptions = null;
      this.fillData();
    },
    getMeasurementDateInterval: function() {
      const measurementLength = this.allMeasurements.length;

      if (measurementLength < 1) return '';

      const endDate = this.allMeasurements[0].date.format('lll');
      const startDate = this.allMeasurements[measurementLength - 1].date.format(
        'lll'
      );

      return `${startDate} - ${endDate}`;
    },
    convertToDate(firebaseDate) {
      const momentDate = moment(firebaseDate);
      return momentDate;
    },
    isValidMeasurement(data) {
      const proteinMgValid = data['milligrams']['protein'] > 10;

      const proteinPercentage = data['percentages']['protein'];
      const proteinPercentageValid =
        proteinPercentage > 0 && proteinPercentage < 100;

      return proteinMgValid && proteinPercentageValid;
    },
    createCompoundData(measurements, compound) {
      return measurements
        .map((data) => {
          return {
            x: data.date,
            y: data[compound],
          };
        })
        .sort((a, b) => a.x - b.x);
    },
    createMovingAverageData(measurements) {
      if (!this.showProteinAverage && !this.showMoistureAverage) {
        return {
          protein: [],
          moisture: [],
        };
      }

      let proteinMovingAverageData = [];
      let moistureMovingAverageData = [];
      const movingAverageStep = this.form.movingAverageStep;

      for (var i = 0; i < measurements.length; i++) {
        let measData = measurements[i];

        // TODO: Speed can be increased with memorizing the old count
        if (this.showProteinAverage) {
          let sum = 0;
          let count = 0;
          for (var j = 0; j < movingAverageStep; j++) {
            if (i + j < measurements.length) {
              count += 1;
              let averageEntry = measurements[i + j];
              sum += averageEntry.protein;
            }
          }
          proteinMovingAverageData.push({
            x: measData.date,
            y: sum / count,
          });
        }

        if (this.showMoistureAverage) {
          let sum = 0;
          let count = 0;
          for (var k = 0; k < movingAverageStep; k++) {
            if (i + k < measurements.length) {
              count += 1;
              let averageEntry = measurements[i + k];
              sum += averageEntry.moisture;
            }
          }
          moistureMovingAverageData.push({
            x: measData.date,
            y: sum / count,
          });
        }
      }

      return {
        protein: proteinMovingAverageData,
        moisture: moistureMovingAverageData,
      };
    },
    fillCompoundChart(validMeasurements, proteins, moistures) {
      const proteinData = this.showProtein ? proteins : [];
      const moistureData = this.showMoisture ? moistures : [];

      const movingAverages = this.createMovingAverageData(validMeasurements);
      const proteinMovingAverageData = movingAverages['protein'];
      const moistureMovingAverageData = movingAverages['moisture'];

      let firstDate;
      if (validMeasurements.length > 0) {
        firstDate = validMeasurements[validMeasurements.length - 1].date;
      } else {
        firstDate = new Date();
      }

      this.chartOptions = {
        showLines: false,
        animation: {
          duration: 5,
        },
        tooltips: {
          mode: 'point',
        },
        legend: {
          position: 'top',
        },
        title: {
          // text: 'Data',
          display: false,
        },
        scales: {
          xAxes: [
            {
              display: true,
              type: 'time',
              distribution: this.chartXAxisStyle,
              legend: false,
              ticks: {
                source: 'auto',
                beginAtZero: false,
                min: firstDate,
                autoSkip: true,
              },
              time: {
                unit: 'minute',
                unitStepSize: 5,
                displayFormats: {
                  minute: 'D.M.YYYY HH:mm',
                },
              },
            },
          ],
        },
        // responsive: true,
        maintainAspectRatio: false,
      };

      this.chartData = {
        labels: [3, 2],
        datasets: [
          {
            label: 'Protein',
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgba(0, 0, 0, 0.0)',
            data: proteinData,
          },
          {
            label: 'Protein moving average',
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgba(0, 0, 0, 0.0)',
            data: proteinMovingAverageData,
            showLine: true,
            pointRadius: 0,
          },
          {
            label: 'Moisture',
            borderColor: 'rgb(54, 162, 235)',
            backgroundColor: 'rgba(0, 0, 0, 0.0)',
            data: moistureData,
          },
          {
            label: 'Moisture moving average',
            borderColor: 'rgb(54, 162, 235)',
            backgroundColor: 'rgba(0, 0, 0, 0.0)',
            data: moistureMovingAverageData,
            showLine: true,
            pointRadius: 0,
          },
        ],
      };
    },
    fillData() {
      this.loaded = false;

      const validMeasurements = this.allMeasurements.filter(
        (data) => data.isValid
      );

      this.proteinData = this.createCompoundData(validMeasurements, 'protein');
      this.moistureData = this.createCompoundData(
        validMeasurements,
        'moisture'
      );

      this.fillCompoundChart(
        validMeasurements,
        this.proteinData,
        this.moistureData
      );

      this.loaded = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.measurements {
  margin-top: 40px;
  margin-bottom: 50px;
  width: 90%;
}
#chart-container {
  margin-bottom: 50px;
  height: 500px;
}

#allMeasurements {
  width: auto;
  margin: auto;
  margin-top: 20px;
}

#header {
  margin-bottom: 20px;
  clear: both;
}

.section {
  margin-top: 50px;
  margin-bottom: 50px;
}
</style>
